import { useState, useMemo } from "react";
import { History, Lock, Eye, Edit3, Download, Trash2 } from "lucide-react";
import { API } from "../../lib/api";
import { fmtDate, fmtL, decisionTone } from "./utils";

const STATUS_FILTERS = ["All", "Draft", "Financial Review", "Under Discussion", "Approved", "Rejected"];

export default function SubmissionsHistory({ subs, onOpen, onDelete, canApprove, editingId, isAdmin }) {
  const [filter, setFilter] = useState("All");

  const counts = useMemo(() => {
    const c = { All: subs.length };
    subs.forEach((s) => { c[s.decision] = (c[s.decision] || 0) + 1; });
    return c;
  }, [subs]);

  const visible = useMemo(
    () => (filter === "All" ? subs : subs.filter((s) => s.decision === filter)),
    [filter, subs]
  );

  return (
    <div className="bg-white border border-zinc-200/80 rounded-xl shadow-sm overflow-hidden flex flex-col h-full min-h-0" data-testid="ic-history-panel">
      <header className="px-4 py-3 border-b border-zinc-100 bg-gradient-to-r from-brand-teal/15 to-transparent">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-zinc-700" />
          <span className="font-display font-bold text-[14px] text-zinc-900">Past IC submissions</span>
          <span className="text-[10px] font-bold bg-brand-ink text-white px-1.5 py-0.5 rounded">{visible.length}{filter !== "All" ? `/${subs.length}` : ""}</span>
        </div>
        <div className="flex flex-wrap gap-1 mt-2" data-testid="ic-history-filter">
          {STATUS_FILTERS.map((s) => {
            const n = counts[s] || 0;
            const active = filter === s;
            const tone = s === "All"
              ? (active ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200")
              : active ? `${decisionTone(s)} border-2 font-bold` : `${decisionTone(s)} opacity-70 hover:opacity-100`;
            return (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`ic-history-filter-${s.replace(/\s/g, "")}`}
                disabled={s !== "All" && n === 0}
                className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border font-medium transition-all whitespace-nowrap ${tone} ${s !== "All" && n === 0 ? "opacity-30 cursor-not-allowed" : ""}`}
              >
                {s} <span className="tabular-nums">· {n}</span>
              </button>
            );
          })}
        </div>
      </header>
      <div className="flex-1 min-h-0 overflow-auto">
        {!visible.length && (
          <div className="p-6 text-center text-[12px] text-zinc-400" data-testid="ic-history-empty">
            {filter === "All" ? "No submissions yet" : `No "${filter}" submissions`}
          </div>
        )}
        {visible.map((s) => {
          const isLocked = s.decision === "Approved" && !isAdmin;
          const isEditing = editingId === s.submission_id;
          return (
            <article key={s.submission_id} className={`px-4 py-3 border-b border-zinc-100 hover:bg-zinc-50/70 ${isEditing ? "bg-amber-50/60" : ""}`} data-testid={`ic-sub-${s.submission_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <h4 className="font-display font-bold text-[13px] text-zinc-900 truncate flex items-center gap-1.5">
                    {s.property_name}
                    {isLocked && <Lock className="w-3 h-3 text-zinc-500" data-testid={`ic-sub-locked-${s.submission_id}`} />}
                  </h4>
                  <div className="text-[10.5px] text-zinc-500 mt-0.5 truncate">
                    {s.city || "—"} · {s.submitted_by_name || s.submitted_by} · {fmtDate(s.updated_at || s.created_at)}
                    {s.updated_at && s.updated_at !== s.created_at && <span className="ml-1 text-amber-700 font-medium">· edited</span>}
                  </div>
                  <div className="text-[10.5px] text-zinc-600 mt-0.5">
                    {s.room_summary?.total_beds || "—"} beds · EBITDA {fmtL(s.base_case?.ebitda)} · ROI {s.base_case?.roi_months ? `${s.base_case.roi_months}mo` : "—"}
                  </div>
                </div>
                <span className={`text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded border whitespace-nowrap ${decisionTone(s.decision)}`}>{s.decision}</span>
              </div>
              <div className="flex gap-1.5 mt-2">
                <button onClick={() => onOpen(s)} data-testid={`ic-sub-open-${s.submission_id}`} className={`text-[11px] px-2 py-1 rounded border inline-flex items-center gap-1 ${isEditing ? "border-amber-400 bg-amber-100 text-amber-900" : "border-zinc-200 text-zinc-700 hover:bg-zinc-50"}`}>
                  {isLocked ? <><Eye className="w-3 h-3" /> View</> : <><Edit3 className="w-3 h-3" /> {isEditing ? "Editing" : "Edit"}</>}
                </button>
                <a href={`${API}/property-ic/submissions/${s.submission_id}/export`} target="_blank" rel="noreferrer" data-testid={`ic-sub-download-${s.submission_id}`} className="text-[11px] text-zinc-700 px-2 py-1 rounded border border-zinc-200 hover:bg-zinc-50 inline-flex items-center gap-1">
                  <Download className="w-3 h-3" /> Report
                </a>
                {canApprove && (
                  <button onClick={() => onDelete(s)} data-testid={`ic-sub-delete-${s.submission_id}`} className="text-[11px] text-rose-600 px-2 py-1 rounded border border-rose-200 hover:bg-rose-50 inline-flex items-center gap-1 ml-auto">
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
